import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import ComicCard from '@/components/feature/ComicCard';
import { useFavorites } from '@/hooks/useFavorites';
import { useSEO } from '@/hooks/useSEO';

export default function Detail() {
  const { comicId } = useParams();
  const comic = comics.find((item) => item.id === comicId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setNotice('');
  }, [comicId]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 2600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const related = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((item) => item.id !== comic.id)
      .map((item) => {
        let score = 0;
        if (item.category === comic.category) score += 2;
        score += item.tags.filter((tag) => comic.tags.includes(tag)).length;
        return { item, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((entry) => entry.item);
  }, [comic]);

  useSEO({
    title: comic ? `${comic.title} - 双女主韩漫全集免费阅读` : '漫画详情',
    description: comic
      ? `${comic.title}（作者：${comic.author}）剧情简介：${comic.description} 双女主韩漫全集免费在线阅读。`
      : '双女主韩漫作品详情与章节列表。',
    keywords: comic
      ? `${comic.title},${comic.tags.join(',')},双女主韩漫,免费阅读,在线漫画`
      : '双女主韩漫,详情',
  });

  if (!comic) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </span>
        <h1 className="text-xl font-bold text-foreground-950">没有找到这部漫画</h1>
        <p className="text-sm text-foreground-500">它可能已被移除，或链接有误。</p>
        <Link
          to="/"
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const category = categories.find((item) => item.slug === comic.category);
  const favorited = isFavorite(comic.id);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-1 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link
          to={`/category/${comic.category}`}
          className="transition-colors hover:text-primary-600"
        >
          {category ? category.name : '分类'}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="line-clamp-1 text-foreground-800">{comic.title}</span>
      </nav>

      <section className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="grid grid-cols-1 gap-6 p-5 md:grid-cols-[240px_1fr] md:p-8">
          <div className="mx-auto w-44 shrink-0 sm:w-52 md:mx-0 md:w-full">
            <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <img
                src={comic.cover}
                alt={`${comic.title}封面`}
                title={`${comic.title} 双女主韩漫免费阅读`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                {comic.status}
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                <i className="ri-refresh-line mr-1"></i>
                {comic.updateDate} 更新
              </span>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800">
                {comic.year} 年
              </span>
            </div>

            <h1 className="mt-3 text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {comic.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-3-line"></i>
                作者：{comic.author}
              </span>
              <span className="flex items-center gap-1 text-primary-600">
                <i className="ri-star-fill"></i>
                {comic.rating.toFixed(1)} 分
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {comic.views} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-book-open-line"></i>
                {comic.latestChapter}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={`/category/${comic.category}`}
                className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-price-tag-3-line mr-1"></i>
                {category ? category.name : '分类'}
              </Link>
              {comic.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <p className="mt-5 text-sm leading-relaxed text-foreground-600">{comic.description}</p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setNotice('阅读页正在建设中，当前为演示效果，章节列表可正常浏览。')}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                开始阅读第 1 话
              </button>
              <button
                type="button"
                onClick={() => {
                  const nowFav = toggleFavorite(comic.id);
                  setNotice(nowFav ? '已加入收藏，可在顶部收藏夹中查看。' : '已取消收藏。');
                }}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-6 py-3 text-sm font-semibold transition-colors cursor-pointer ${
                  favorited
                    ? 'border-primary-300 bg-primary-100 text-primary-700'
                    : 'border-background-300 bg-background-50 text-foreground-800 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                {favorited ? '已收藏' : '收藏本作'}
              </button>
              <button
                type="button"
                onClick={() => setNotice('已复制分享链接（演示）。')}
                className="flex h-12 w-12 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700 cursor-pointer"
                aria-label="分享"
              >
                <i className="ri-share-forward-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-list-check-2 text-primary-500"></i>
              章节目录
            </h2>
            <span className="text-xs text-foreground-400">共 {comic.chapters.length} 话</span>
          </div>
          <ul className="flex max-h-[560px] flex-col gap-1.5 overflow-y-auto pr-1">
            {comic.chapters.map((chapter) => (
              <li key={chapter.id}>
                <button
                  type="button"
                  onClick={() => setNotice(`即将打开「${chapter.title}」（阅读页演示中）。`)}
                  className="group flex w-full items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <i className="ri-book-2-line text-accent-500"></i>
                    <span className="line-clamp-1 text-sm font-medium text-foreground-800 group-hover:text-primary-700">
                      {chapter.title}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-foreground-400">
                    {chapter.date}
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <aside>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-compass-3-line text-accent-600"></i>
              相关推荐
            </h2>
            <div className="flex flex-col gap-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
                >
                  <span className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-100">
                    <img
                      src={item.cover}
                      alt={`${item.title}封面`}
                      title={`${item.title} 双女主韩漫免费阅读`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-1 block text-sm font-semibold text-foreground-900 group-hover:text-primary-600">
                      {item.title}
                    </span>
                    <span className="line-clamp-1 block text-xs text-foreground-500">
                      {item.latestChapter}
                    </span>
                    <span className="mt-1 flex items-center gap-1 text-[11px] text-primary-600">
                      <i className="ri-star-fill"></i>
                      {item.rating.toFixed(1)}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <section className="mt-8">
        <div className="mb-5 flex items-end justify-between">
          <h2 className="text-xl font-bold text-foreground-950">
            {category ? category.name : ''}同类双女主韩漫
          </h2>
          <Link
            to={`/category/${comic.category}`}
            className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            查看分类
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-4">
          {related.map((item) => (
            <ComicCard key={item.id} comic={item} />
          ))}
        </div>
      </section>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] flex max-w-[90vw] -translate-x-1/2 items-center gap-2 rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50 animate-fade-up">
          <i className="ri-information-line text-primary-300"></i>
          <span>{notice}</span>
        </div>
      )}
    </div>
  );
}